/**
 * Helpers de formatacao de tempo relativo (ex: "5min atras", "ontem").
 *
 * Usados em UI onde queremos contextualizar datas sem mostrar timestamp completo.
 * Ex: presence (last_seen_at), historico de mensagens, atividade.
 *
 * Tipos:
 *   - relativeShort:  "agora" / "5min" / "3h" / "2d" / "15/05"  (compacto p/ chips e listas)
 *   - relativeLong:   "agora mesmo" / "ha 5 minutos" / "ha 3 horas" / "ontem" / "15/05/2026"
 *     (mais legivel pra texto secundario, ex: "Ultimo login: ha 5 minutos")
 */

/**
 * Formato compacto (chips, listas, badges).
 * Compativel com a funcao `relativeTime` que existia em ChatDesktop.
 */
export function relativeShort(iso: string | null | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  // > 7 dias: mostra data DD/MM
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Formato longo (texto secundario, contextual).
 * Ex: "Ultimo login: ha 5 minutos"
 */
export function relativeLong(iso: string | null | undefined): string {
  if (!iso) return 'nunca';
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'agora mesmo';
  if (m === 1) return 'ha 1 minuto';
  if (m < 60) return `ha ${m} minutos`;
  const h = Math.floor(m / 60);
  if (h === 1) return 'ha 1 hora';
  if (h < 24) return `ha ${h} horas`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'ontem';
  if (d < 7) return `ha ${d} dias`;
  if (d < 30) {
    const w = Math.floor(d / 7);
    return w === 1 ? 'ha 1 semana' : `ha ${w} semanas`;
  }
  // > 30 dias: data DD/MM/YYYY
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Verifica se um ISO esta dentro de uma janela de "online" (default 5min).
 * Usado pelo StudentCard pra mostrar bolinha verde.
 */
export function isOnline(iso: string | null | undefined, windowMs: number = 5 * 60 * 1000): boolean {
  if (!iso) return false;
  const diff = Date.now() - new Date(iso).getTime();
  return diff >= 0 && diff < windowMs;
}

/**
 * Formato "humano grosso" pra contexto do PT visualizando a lista de alunos.
 * Em vez de "ha 42 minutos", retorna categorias que respondem direto a "o aluno
 * sumiu?" — hoje / ontem / esta semana / semana passada / um mes atras /
 * ha muito tempo. Sem precisao excessiva pra evitar uso pra vigilancia minuciosa.
 *
 * NAO usar no backoffice (admin Aratek precisa de granularidade fina pra gestao).
 *
 * Limites (usando calendario LOCAL, nao janelas deslizantes):
 *   - mesmo dia (apos 5min) → "hoje"
 *   - dia anterior          → "ontem"
 *   - 2-7 dias              → "esta semana"
 *   - 8-14 dias             → "semana passada"
 *   - 15-60 dias            → "um mes atras"
 *   - > 60 dias             → "ha muito tempo"
 */
export function relativeCoarse(iso: string | null | undefined): string {
  if (!iso) return 'nunca';
  const then = new Date(iso);
  const now = new Date();
  // Diferenca em dias calendaristicos (zera horas pra comparar so a data).
  const dayThen = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const dayNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dDays = Math.round((dayNow.getTime() - dayThen.getTime()) / 86_400_000);
  if (dDays <= 0) return 'hoje';
  if (dDays === 1) return 'ontem';
  if (dDays <= 7) return 'esta semana';
  if (dDays <= 14) return 'semana passada';
  if (dDays <= 60) return 'um mês atrás';
  return 'há muito tempo';
}
